import React from 'react';

import { useGetLists } from '@vlabs/api/artemy/client';
import type { DepartmentId, ListId } from '@vlabs/api/artemy/model';
import { MoreHorizontalIcon } from '@vlabs/icons';
import { DropdownMenu } from '@vlabs/uikit';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button';

import {
  AssignListsToAllInDepartmentSwitch,
  DeleteListsDropdownItem,
  MoveListsToDepartmentDropdownItem,
} from '@vlabs/monokit/features';

import st from './DepartmentListList.module.sass';
import { useDepartmentListListTranslation } from './i18n';
import { DepartmentList } from '../../../entities/lists/@x/departments';
import { FoldableEntityEmpty } from '../../../shared/ui/foldable-entity/FoldableEntityEmpty';

interface OwnershipListActionsProps {
  departmentId: DepartmentId;
  listId: ListId;
}

function OwnershipListActions({
  departmentId,
  listId,
}: OwnershipListActionsProps): React.JSX.Element {
  const { t } = useDepartmentListListTranslation();

  return (
    <div className={st.Actions}>
      <AssignListsToAllInDepartmentSwitch
        departmentId={departmentId}
        listIds={[listId]}
      />
      <DropdownMenu.Root>
        <DropdownMenu.Trigger
          render={(
            <IconButton
              icon={<MoreHorizontalIcon />}
              kind="secondary"
              title={t('actions')}
              variant="flat"
            />
          )}
        />
        <DropdownMenu.Portal>
          <DropdownMenu.Positioner
            align="end"
            side="bottom"
          >
            <DropdownMenu.Popup>
              <MoveListsToDepartmentDropdownItem
                departmentId={departmentId}
                listIds={[listId]}
              />
              <DeleteListsDropdownItem listIds={[listId]} />
            </DropdownMenu.Popup>
          </DropdownMenu.Positioner>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}

interface OwnershipDepartmentListsProps {
  departmentId: DepartmentId;
}

function OwnershipDepartmentLists({
  departmentId,
}: OwnershipDepartmentListsProps): React.JSX.Element {
  const getLists = useGetLists({ owner_department_ids: [departmentId] });
  const lists = getLists.data?.lists ?? [];

  if (lists.length === 0) {
    return <FoldableEntityEmpty />;
  }

  return (
    <React.Fragment>
      {lists.map(({ list_id: listId }) => {
        return listId && (
          <DepartmentList
            actions={(
              <OwnershipListActions
                departmentId={departmentId}
                listId={listId}
              />
            )}
            key={listId}
            listId={listId}
          />
        );
      })}
    </React.Fragment>
  );
}

export { OwnershipDepartmentLists };
